'use client'

import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import Image from 'next/image'

const titleStyle =
  'tracking-[-0.05em] text-main-light-1 dark:text-main-dark-1 text-center'
const descriptionStyle =
  'text-main-light-3 dark:text-main-dark-3 text-center sm:px-0'
const MobileLinkWrapperStyle =
  'relative rounded-5xl bg-cover min-w-[300px] max-w-[300px] h-[300px] py-[40px] px-[42px]'
const DesktopLinkWrapperStyle =
  'relative rounded-5xl bg-cover flex-1 min-w-[334px] max-w-[334px] h-[278px] py-[36px] px-[34px]'
const linkTitleStyle =
  'font-pretendard-semibold tracking-[-0.02em] text-main-light-1 leading-tight text-[26px] mb-3'
const linkDescriptionStyle = 'text-main-light-2 text-[16px] leading-[24px]'

const renderIcon = (icon: string) => {
  switch (icon) {
    case 'megaphone':
      return (
        <Image
          src={'/images/Megaphone.svg'}
          alt="megaphone-icon"
          width={128}
          height={128}
          className="w-[100px] sm:w-[128px]"
        />
      )
    case 'questionMark':
      return (
        <Image
          src={'/images/QuestionMark.svg'}
          alt="questionmark-icon"
          width={95}
          height={95}
          className="w-[70px] sm:w-[95px]"
        />
      )
    case 'post':
      return (
        <Image
          src={'/images/Post.svg'}
          alt="post-icon"
          width={150}
          height={150}
          className="w-[100px] sm:w-[150px]"
        />
      )
    default:
      return null
  }
}

const BannerSection = () => {
  const { t } = useTranslation('home')

  const renderTextWithLineBreaks = (text: string) => {
    return text.split('\n').map((line, index) => <div key={index}>{line}</div>)
  }

  return (
    <section className="mx-auto">
      {/* 모바일 버전 */}
      <article className="flex flex-col gap-2 sm:hidden">
        <div className="mb-[22px]">
          <h2 className={`${titleStyle} mt-[158px] text-[24px]`}>
            {t('banner_data.title')}
          </h2>
          <p className={`${descriptionStyle} text-[15px] leading-[22px]`}>
            {renderTextWithLineBreaks(t('banner_data.description'))}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center gap-[11px]">
          <Link
            href={t('banner_data.link_1.href')}
            className={`${t(
              'banner_data.link_1.bgClass'
            )} ${MobileLinkWrapperStyle}`}
          >
            <h2 className={`${linkTitleStyle}`}>
              {t('banner_data.link_1.title')}
            </h2>
            <p className={`${linkDescriptionStyle}`}>
              {t('banner_data.link_1.description')}
            </p>
            <div className="absolute bottom-11 right-10">
              {renderIcon(t('banner_data.link_1.icon'))}
            </div>
          </Link>
          <Link
            href={t('banner_data.link_2.href')}
            className={`${t(
              'banner_data.link_2.bgClass'
            )} ${MobileLinkWrapperStyle}`}
          >
            <h2 className={`${linkTitleStyle}`}>
              {t('banner_data.link_2.title')}
            </h2>
            <p className={`${linkDescriptionStyle}`}>
              {t('banner_data.link_2.description')}
            </p>
            <div className="absolute bottom-10 right-10">
              {renderIcon(t('banner_data.link_2.icon'))}
            </div>
          </Link>
          <Link
            href={t('banner_data.link_3.href')}
            className={`${t(
              'banner_data.link_3.bgClass'
            )} ${MobileLinkWrapperStyle}`}
          >
            <h2 className={`${linkTitleStyle}`}>
              {t('banner_data.link_3.title')}
            </h2>
            <p className={`${linkDescriptionStyle}`}>
              {renderTextWithLineBreaks(t('banner_data.link_3.description'))}
            </p>
            <div className="absolute bottom-[40px] right-[45px]">
              {renderIcon(t('banner_data.link_3.icon'))}
            </div>
          </Link>
        </div>
      </article>

      {/* 데스크탑 버전 */}
      <article className="mt-[124px] hidden sm:flex">
        <div className="flex flex-col items-center justify-center">
          {/* 메인 배너 */}
          <div className="flex flex-col items-center justify-center">
            <h2 className={`${titleStyle} text-[40px] leading-tight`}>
              {t('banner_data.title')}
            </h2>
            <p
              className={`${descriptionStyle} mt-[24px] text-[32px] leading-tight`}
            >
              {t('banner_data.description')}
            </p>
          </div>

          {/* 링크 카드 */}
          <div className="mt-[73px] flex max-w-[1024px] flex-wrap items-center justify-center gap-[10px]">
            <Link
              href={t('banner_data.link_1.href')}
              className={`${t(
                'banner_data.link_1.bgClass'
              )} ${DesktopLinkWrapperStyle}`}
            >
              <h2 className={`${linkTitleStyle}`}>
                {t('banner_data.link_1.title')}
              </h2>
              <p className={`${linkDescriptionStyle}`}>
                {t('banner_data.link_1.description')}
              </p>
              <div className="absolute bottom-11 right-10">
                {renderIcon(t('banner_data.link_1.icon'))}
              </div>
            </Link>
            <Link
              href={t('banner_data.link_2.href')}
              className={`${t(
                'banner_data.link_2.bgClass'
              )} ${DesktopLinkWrapperStyle}`}
            >
              <h2 className={`${linkTitleStyle}`}>
                {t('banner_data.link_2.title')}
              </h2>
              <p className={`${linkDescriptionStyle}`}>
                {t('banner_data.link_2.description')}
              </p>
              <div className="absolute bottom-10 right-10">
                {renderIcon(t('banner_data.link_2.icon'))}
              </div>
            </Link>
            <Link
              href={t('banner_data.link_3.href')}
              className={`${t(
                'banner_data.link_3.bgClass'
              )} ${DesktopLinkWrapperStyle}`}
            >
              <h2 className={`${linkTitleStyle}`}>
                {t('banner_data.link_3.title')}
              </h2>
              <p className={`${linkDescriptionStyle}`}>
                {renderTextWithLineBreaks(t('banner_data.link_3.description'))}
              </p>
              <div className="absolute bottom-[40px] right-[45px]">
                {renderIcon(t('banner_data.link_3.icon'))}
              </div>
            </Link>
          </div>
        </div>
      </article>
    </section>
  )
}

export default BannerSection
